import {
  createOutputTextItem,
  createRecipeChatItem,
} from '$tablet-app/src/app/chat/ChatItem/functions';
import { ChatItem } from '$tablet-app/src/app/chat/ChatItem/types';
import {
  ListRecipesQueryVariables,
  ModelRecipeFilterInput,
} from '@akin-home/amplify';
import { getRandomItemFromList, shuffle } from '@akin-home/arrays';
import { listAllOptionModels } from '@akin-home/options/frontend';
import {
  listAllRecipesQuery,
  recipeGraphqlToModel,
} from '@akin-home/recipes/frontend';
import {
  CommandTypes,
  ShowEasyRecipeItemCommand,
  ShowQuickRecipeItemCommand,
  ShowRecipeItemCommand,
} from '../types';

interface Filter {
  specialDietIds: string[] | null;
  prepTimeIds: string[] | null;
  skillLevelId: string | null;
  mainIngredientId: string | null;
  mealTypeId: string | null;
  searchField: string | null;
}

/** APIS for fetching and searching recipe items */
const handleRecipeItemCommand = async (
  command:
    | ShowRecipeItemCommand
    | ShowQuickRecipeItemCommand
    | ShowEasyRecipeItemCommand,
  {
    addItem,
  }: {
    addItem: (item: ChatItem | ChatItem[]) => void;
  }
): Promise<void> => {
  const { data } = command;

  if (!data) return;

  const options = await listAllOptionModels();

  const MEAL_TYPE_OPTIONS = [
    {
      id: 'Breakfast',
      label: 'Breakfast',
      type: 'meal_type',
      code: 'meal_type_breakfast',
    },
    {
      id: 'Lunch',
      label: 'Lunch',
      type: 'meal_type',
      code: 'meal_type_lunch',
    },
    {
      id: 'Dinner',
      label: 'Dinner',
      type: 'meal_type',
      code: 'meal_type_dinner',
    },
    {
      id: 'Snack',
      label: 'Snack',
      type: 'meal_type',
      code: 'meal_type_snack',
    },
  ];

  const INGREDIENT_ENTITY_OPTIONS = [
    { label: 'fish', codes: ['main_ingredient_fish'] },
    {
      label: 'seafood',
      codes: ['main_ingredient_fish', 'main_ingredient_seafood'],
    },
    { label: 'beef', codes: ['main_ingredient_beef'] },
    { label: 'pork', codes: ['main_ingredient_pork'] },
    { label: 'chicken', codes: ['main_ingredient_chicken'] },
    { label: 'lamb', codes: ['main_ingredient_lamb'] },
    {
      label: 'meat',
      codes: [
        'main_ingredient_beef',
        'main_ingredient_pork',
        'main_ingredient_chicken',
        'main_ingredient_lamb',
      ],
    },
  ];

  const isIngredientEntity = (label: string) => {
    return INGREDIENT_ENTITY_OPTIONS.map((o) => o.label).includes(
      label.toLowerCase()
    );
  };

  const containsAllSpecialDietIds = (filterItems: string[]) => {
    return filterItems.map(
      (item) =>
        ({
          specialDietIDs: { contains: item },
        } as ModelRecipeFilterInput)
    );
  };

  const containsAnyPrepTimeIds = (filterItems: string[]) => {
    const arr = [];
    for (const item of filterItems) {
      arr.push({ prepTimeID: { eq: item } } as ModelRecipeFilterInput);
    }
    return arr;
  };

  const containsAnyMainIngredientIds = (filterItems: string[]) => {
    const arr = [];
    for (const item of filterItems) {
      arr.push({ mainIngredientID: { eq: item } } as ModelRecipeFilterInput);
    }
    return arr;
  };

  const mapLabelToId = (type: string, label: string) => {
    const id = options.find(
      (o) => o.type === type && o.label.toLowerCase() === label.toLowerCase()
    )?.id;

    return id === undefined ? null : id;
  };

  const mapCodeToId = (type: string, code: string) => {
    const id = options.find((o) => o.type === type && o.code === code)?.id;
    return id === undefined ? '' : id;
  };

  const createRecipeFilter = ({
    specialDietIds,
    prepTimeIds,
    skillLevelId,
    mainIngredientId,
    mealTypeId,
    searchField,
  }: Filter): ListRecipesQueryVariables => ({
    filter: {
      and:
        specialDietIds !== null
          ? containsAllSpecialDietIds(specialDietIds)
          : undefined,
      or:
        prepTimeIds !== null ? containsAnyPrepTimeIds(prepTimeIds) : undefined,
      skillLevelID: skillLevelId !== null ? { eq: skillLevelId } : undefined,
      mainIngredientID:
        mainIngredientId !== null ? { eq: mainIngredientId } : undefined,
      mealTypes: mealTypeId !== null ? { contains: mealTypeId } : undefined,
      searchField: searchField !== null ? { contains: searchField } : undefined,
      inProgress: { ne: true },
    },
  });

  const createIngredientEntityFilter = (ingredientEntity: any) => {
    const mainIngredientCodes = INGREDIENT_ENTITY_OPTIONS.find(
      (o) => o.label === ingredientEntity.toLowerCase()
    )?.codes;
    const mainIngredientIds = mainIngredientCodes?.map((c) =>
      mapCodeToId('main_ingredient', c)
    );
    if (mainIngredientIds !== undefined) {
      return {
        filter: {
          or: containsAnyMainIngredientIds(mainIngredientIds),
          inProgress: { ne: true },
        },
      };
    }
  };

  const addRandomRecipe = async (filter?: ListRecipesQueryVariables) => {
    const recipes = await listAllRecipesQuery(filter);
    const recipe = recipeGraphqlToModel(getRandomItemFromList(recipes));

    addItem(
      createRecipeChatItem({
        items: [
          {
            item_id: recipe.id,
            name: recipe.name,
            image: recipe.image,
            special_diets: recipe.specialDiets?.map((s) => s.label),
          },
        ],
      })
    );
  };

  const addRandomRecipes = async (
    num_recipes: number,
    filter?: ListRecipesQueryVariables,
    text_chat_item?: ChatItem
  ) => {
    const recipes = await listAllRecipesQuery(filter);
    const number_of_recipes_to_add = Math.min(num_recipes, recipes.length);
    const recipes_to_add = shuffle(recipes).slice(0, number_of_recipes_to_add);

    const chat_item = createRecipeChatItem({
      items: recipes_to_add.map(recipeGraphqlToModel).map((recipe) => ({
        item_id: recipe.id,
        name: recipe.name,
        image: recipe.image,
        special_diets: recipe.specialDiets?.map((s) => s.label),
      })),
    });

    if (text_chat_item) {
      addItem(text_chat_item);
    }

    addItem(chat_item);

    return recipes_to_add.length > 0;
  };

  /** Fetch a random recipe */
  if (
    command.type === CommandTypes.SHOW_RECIPE_ITEM &&
    data.tag === undefined &&
    data.meal_type === undefined
  ) {
    addRandomRecipe();
    return;
  } else if (
    /** Suggest a recipe with particular entities */
    command.type === CommandTypes.SHOW_RECIPE_ITEM &&
    data.tag !== undefined &&
    isIngredientEntity(data.tag)
  ) {
    // Check specific ingredient type
    if (data.tag2 !== undefined) {
      const ingredientEntityFilter = createIngredientEntityFilter(data.tag2);
      const searchFieldFilter = createRecipeFilter({
        searchField: data.tag2,
        mainIngredientId: null,
        specialDietIds: null,
        mealTypeId: null,
        prepTimeIds: null,
        skillLevelId: null,
      });
      const filter =
        ingredientEntityFilter == null
          ? searchFieldFilter
          : ingredientEntityFilter;

      let chatTextItem = createOutputTextItem(
        `Here are some ${data.tag2.toLowerCase()} recipes you might like`
      );

      const doRecipesExist = await addRandomRecipes(3, filter, chatTextItem);

      // If no recipes for specific ingredient, default to main ingredient
      if (!doRecipesExist) {
        if (!isIngredientEntity(data.tag2)) {
          chatTextItem = createOutputTextItem(
            `I couldn't find any ${data.tag2.toLowerCase()} recipes, but here are some other ${
              data.tag
            } recipes you might like`
          );
        }
        const filter = createIngredientEntityFilter(data.tag);
        addRandomRecipes(3, filter, chatTextItem);
      }
    }
  } else if (
    /** Suggest a recipe with tags */
    command.type == CommandTypes.SHOW_RECIPE_ITEM &&
    (data.tag !== undefined || data.meal_type !== undefined)
  ) {
    const specialDietId1 =
      data.tag !== undefined ? mapLabelToId('special_diet', data.tag) : null;
    const specialDietId2 =
      data.tag2 !== undefined ? mapLabelToId('special_diet', data.tag2) : null;
    const specialDietIds =
      specialDietId1 !== null
        ? specialDietId2 !== null
          ? [specialDietId1, specialDietId2]
          : [specialDietId1]
        : null;

    const mainIngredientId1 =
      data.tag !== undefined ? mapLabelToId('main_ingredient', data.tag) : null;
    const mainIngredientId2 =
      data.tag2 !== undefined
        ? mapLabelToId('main_ingredient', data.tag2)
        : null;
    const mainIngredientId =
      mainIngredientId1 !== null
        ? mainIngredientId1
        : mainIngredientId2 !== null
        ? mainIngredientId2
        : null;

    const mealTypeOption =
      data.meal_type !== undefined
        ? MEAL_TYPE_OPTIONS.find(
            (o) => o.id.toLowerCase() === data.meal_type?.toLowerCase()
          )
        : undefined;
    const mealTypeId = mealTypeOption !== undefined ? mealTypeOption.id : null;

    const filter = createRecipeFilter({
      specialDietIds,
      mainIngredientId,
      mealTypeId,
      prepTimeIds: null,
      skillLevelId: null,
      searchField: null,
    });
    addRandomRecipes(3, filter);
  } else if (command.type === CommandTypes.SHOW_QUICK_RECIPE_ITEM) {
    /** Suggest a quick recipe */
    const prepTimeUnder10 = mapCodeToId(
      'preparation_time',
      'preparation_time_less_than_10'
    );
    const prepTime15 = mapCodeToId('preparation_time', 'preparation_time_15');
    const prepTime30 = mapCodeToId('preparation_time', 'preparation_time_30');

    const specialDietId =
      data.tag !== undefined ? mapLabelToId('special_diet', data.tag) : null;
    const specialDietIds = specialDietId !== null ? [specialDietId] : null;

    const mainIngredientId =
      data.tag !== undefined ? mapLabelToId('main_ingredient', data.tag) : null;

    const mealTypeOption =
      data.meal_type !== undefined
        ? MEAL_TYPE_OPTIONS.find(
            (o) => o.id.toLowerCase() === data.meal_type?.toLowerCase()
          )
        : undefined;
    const mealTypeId = mealTypeOption !== undefined ? mealTypeOption.id : null;

    const filter = createRecipeFilter({
      prepTimeIds: [prepTimeUnder10, prepTime15, prepTime30],
      specialDietIds,
      mainIngredientId,
      mealTypeId,
      skillLevelId: null,
      searchField: null,
    });
    addRandomRecipes(3, filter);
    return;
  } else if (command.type == CommandTypes.SHOW_EASY_RECIPE_ITEM) {
    /** Suggest an easy recipe */
    const skillLevelId = mapCodeToId('skill_level', 'skill_level_easy');

    const specialDietId =
      data.tag !== undefined ? mapLabelToId('special_diet', data.tag) : null;
    const specialDietIds = specialDietId !== null ? [specialDietId] : null;

    const mainIngredientId =
      data.tag !== undefined ? mapLabelToId('main_ingredient', data.tag) : null;

    const mealTypeOption =
      data.meal_type !== undefined
        ? MEAL_TYPE_OPTIONS.find(
            (o) => o.id.toLowerCase() === data.meal_type?.toLowerCase()
          )
        : undefined;
    const mealTypeId = mealTypeOption !== undefined ? mealTypeOption.id : null;

    const filter = createRecipeFilter({
      skillLevelId,
      specialDietIds,
      mainIngredientId,
      mealTypeId,
      prepTimeIds: null,
      searchField: null,
    });
    addRandomRecipes(3, filter);
    return;
  }
};

export default handleRecipeItemCommand;
